import {
  DiningSectionWithTables,
  GroupedOrderItem,
} from '@/renderer/types/sales';
import { OutletSalesReport } from '@/renderer/types/report';
import { AccessControlCode, FULL_ACCESS_USER_ROLES } from '@shared/constants';
import { BusinessType, UserRole } from '@shared/types';
import { createSelector } from '@reduxjs/toolkit';
import {
  selectLocalOpenDineTableIds,
  selectVisibleSections,
  selectVisibleTables,
} from '../sections/sectionSelectors';
import { RunningOrderState } from './runningOrderTypes';

interface RunningOrderRootState {
  runningOrder: RunningOrderState;
  auth?: {
    user?: {
      role?: UserRole;
      outlet?: {
        id?: string;
        name?: string;
        day_open?: string | null;
        business_type?: BusinessType;
        kot_tracking_enabled?: boolean;
      } | null;
    } | null;
    accessControlCodes?: string[];
  };
}

export const selectRunningOrderState = (state: RunningOrderRootState) =>
  state.runningOrder;

export const selectRunningOrderError = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.error,
);

export const selectOpenDiningSectionId = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.openDiningSectionId,
);

export const selectDiningSectionsWithTables = createSelector(
  [selectVisibleSections, selectVisibleTables, selectLocalOpenDineTableIds],
  (sections, tables, localOpenTableIds): DiningSectionWithTables[] =>
    sections
      .map((section) => {
        const sectionTables = tables.filter(
          (table) => table.section_id === section.id,
        );
        // Local SQLite is authoritative for open-order status once data has
        // loaded (localOpenTableIds.length > 0). This ensures offline settlements
        // immediately unhighlight the table without waiting for a Supabase round-trip.
        // Fall back to Supabase's has_running_order only on the first render,
        // before syncLocalTableIds has populated the local list.
        const sectionTablesWithLocal = sectionTables.map((table) => ({
          ...table,
          has_running_order:
            localOpenTableIds.length > 0
              ? localOpenTableIds.includes(table.id)
              : table.has_running_order,
        }));
        const runningOrderTableCount = sectionTablesWithLocal.filter(
          (table) => table.has_running_order,
        ).length;

        return {
          ...section,
          tables: sectionTablesWithLocal,
          runningOrderTableCount,
          hasRunningOrders: runningOrderTableCount > 0,
        };
      })
      .filter((section) => section.tables.length > 0),
);

export const selectDiningSectionIds = createSelector(
  [selectDiningSectionsWithTables],
  (sections) => sections.map((section) => section.id),
);

export const selectEffectiveOpenDiningSectionId = createSelector(
  [selectOpenDiningSectionId, selectDiningSectionsWithTables],
  (openDiningSectionId, sections) => {
    if (
      openDiningSectionId &&
      sections.some((section) => section.id === openDiningSectionId)
    ) {
      return openDiningSectionId;
    }

    return sections[0]?.id ?? null;
  },
);

export const selectDiningSectionById = (sectionId: number) =>
  createSelector(
    [selectDiningSectionsWithTables],
    (sections) => sections.find((section) => section.id === sectionId) || null,
  );

export const selectSelectedTable = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.selectedTable,
);

export const selectSelectedTableId = createSelector(
  [selectSelectedTable],
  (selectedTable) => selectedTable?.id ?? null,
);

export const selectSelectedTakeAwayId = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.selectedTakeAwayId,
);

export const selectTakeAwayOrders = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.takeAwayOrders,
);

export const selectIsTakeAwayOrdersLoading = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isTakeAwayOrdersLoading,
);

export const selectIsCreatingTakeAwayOrder = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isCreatingTakeAwayOrder,
);

export const selectSelectedOrder = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.selectedOrder,
);

export const selectSelectedOrderItems = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.selectedOrderItems,
);

export const selectGroupedOrderItems = createSelector(
  [selectSelectedOrderItems],
  (items): GroupedOrderItem[] => {
    const groupedMap = new Map<string, GroupedOrderItem>();

    items
      .filter((item) => item.status !== 'CANCELLED')
      .forEach((item) => {
        const key = item.menu_item_id;
        const existing = groupedMap.get(key);

        if (!existing) {
          groupedMap.set(key, {
            key,
            menuItemId: item.menu_item_id,
            name: item.name,
            quantity: item.quantity,
            amount: item.amount,
            pendingCount: item.status === 'PENDING' ? 1 : 0,
            lineCount: 1,
            hasPending: item.status === 'PENDING',
            items: [item],
          });
          return;
        }

        existing.quantity += item.quantity;
        existing.amount += item.amount;
        existing.pendingCount += item.status === 'PENDING' ? 1 : 0;
        existing.lineCount += 1;
        existing.hasPending = existing.pendingCount > 0;
        existing.items.push(item);
      });

    return Array.from(groupedMap.values()).sort((a, b) => {
      if (a.hasPending !== b.hasPending) {
        return a.hasPending ? -1 : 1;
      }

      if (a.pendingCount !== b.pendingCount) {
        return b.pendingCount - a.pendingCount;
      }

      return a.name.localeCompare(b.name);
    });
  },
);

export const selectSelectedOrderItemGroupKey = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.selectedOrderItemGroupKey,
);

export const selectIsOrderItemsModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isOrderItemsModalOpen,
);

export const selectIsQuickActionsModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isQuickActionsModalOpen,
);

export const selectIsOpenNewDayModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isOpenNewDayModalOpen,
);

export const selectIsSalesReportModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isSalesReportModalOpen,
);

export const selectIsLoadingSalesReport = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isLoadingSalesReport,
);

export const selectIsOpeningNewDay = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isOpeningNewDay,
);

export const selectIsExportingSalesReport = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isExportingSalesReport,
);

export const selectIsSendingReportEmail = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isSendingReportEmail,
);

export const selectOpenNewDayTargetDate = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.openNewDayTargetDate,
);

export const selectSalesReport = createSelector(
  [selectRunningOrderState],
  (runningOrder): OutletSalesReport | null => runningOrder.salesReport,
);

const formatBusinessDayLabel = (value: string): string =>
  new Date(`${value}T00:00:00`).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

export const shiftIsoDateByDays = (value: string, days: number): string => {
  const parsedDate = new Date(`${value}T00:00:00`);
  parsedDate.setDate(parsedDate.getDate() + days);
  const year = parsedDate.getFullYear();
  const month = `${parsedDate.getMonth() + 1}`.padStart(2, '0');
  const day = `${parsedDate.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getTodayIsoDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const selectCurrentOutletName = (state: RunningOrderRootState) =>
  state.auth?.user?.outlet?.name || 'Outlet';

export const selectCurrentBusinessDayValue = (state: RunningOrderRootState) =>
  state.auth?.user?.outlet?.day_open || getTodayIsoDate();

const selectAuthUserRole = (state: RunningOrderRootState) =>
  state.auth?.user?.role ?? null;

const selectAuthAccessControlCodes = (state: RunningOrderRootState) =>
  state.auth?.accessControlCodes ?? [];

const selectHasUnrestrictedAccess = createSelector(
  [selectAuthUserRole],
  (role) => Boolean(role && FULL_ACCESS_USER_ROLES.includes(role as UserRole)),
);

const selectHasAccessControl = (code: AccessControlCode | string) =>
  createSelector(
    [selectHasUnrestrictedAccess, selectAuthAccessControlCodes],
    (hasUnrestrictedAccess, accessControlCodes) =>
      hasUnrestrictedAccess || accessControlCodes.includes(code),
  );

export const selectCanCreateOrders = selectHasAccessControl(
  AccessControlCode.SALES_ORDERS_CREATE,
);

export const selectCanOpenNewDay = selectHasAccessControl(
  AccessControlCode.SALES_DAY_OPEN,
);

export const selectCanPrintBills = selectHasAccessControl(
  AccessControlCode.SALES_BILLS_PRINT,
);

export const selectCanReprintKots = selectHasAccessControl(
  AccessControlCode.SALES_KOTS_REPRINT,
);

export const selectCanShiftOrders = selectHasAccessControl(
  AccessControlCode.SALES_ORDER_ITEMS_SHIFT,
);

export const selectCanOfferDiscounts = selectHasAccessControl(
  AccessControlCode.SALES_DISCOUNTS_OFFER,
);

export const selectCanDeletePendingOrderItems = selectHasAccessControl(
  AccessControlCode.SALES_ORDER_ITEMS_DELETE_PENDING,
);

export const selectCanCancelConfirmedOrderItems = selectHasAccessControl(
  AccessControlCode.SALES_ORDER_ITEMS_CANCEL,
);

export const selectCurrentBusinessDayLabel = createSelector(
  [selectCurrentBusinessDayValue],
  (dayOpen) => formatBusinessDayLabel(dayOpen),
);

export const selectNextBusinessDayValue = createSelector(
  [selectCurrentBusinessDayValue],
  (dayOpen) => shiftIsoDateByDays(dayOpen, 1),
);

export const selectSelectedGroupedOrderItem = createSelector(
  [selectGroupedOrderItems, selectSelectedOrderItemGroupKey],
  (groupedItems, selectedGroupKey) =>
    groupedItems.find((groupedItem) => groupedItem.key === selectedGroupKey) ||
    null,
);

export const selectSelectedGroupedOrderItemsSorted = createSelector(
  [selectSelectedGroupedOrderItem],
  (groupedItem) => {
    if (!groupedItem) return [];
    return [...groupedItem.items].sort((a, b) =>
      a.created_at > b.created_at ? -1 : 1,
    );
  },
);

export const selectOrderDisplayTotal = createSelector(
  [selectSelectedOrder],
  (selectedOrder) => Math.max(selectedOrder?.net_amount || 0, 0),
);

export const selectSelectedOrderTotals = createSelector(
  [selectSelectedOrder, selectGroupedOrderItems],
  (selectedOrder, groupedItems) => {
    const fallbackSubtotal = groupedItems.reduce(
      (sum, item) => sum + item.amount,
      0,
    );
    const subtotal = selectedOrder?.total_amount ?? fallbackSubtotal;
    const discount = selectedOrder?.discount_amount ?? 0;
    const finalAmount = Math.max(selectedOrder?.net_amount ?? subtotal, 0);
    const hasDiscount = discount > 0;

    return {
      subtotal,
      discount,
      finalAmount,
      hasDiscount,
    };
  },
);

export const selectIsOrderLoading = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isOrderLoading,
);

export const selectCreateOrderTargetTable = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.createOrderTargetTable,
);

export const selectIsCreateOrderModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isCreateOrderModalOpen,
);

export const selectIsCreatingOrder = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isCreatingOrder,
);

export const selectMenuItemForModal = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.menuItemForModal,
);

export const selectIsAddItemModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isAddItemModalOpen,
);

export const selectIsAddingOrderItem = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isAddingOrderItem,
);

export const selectIsDiscountModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isDiscountModalOpen,
);

export const selectIsPreparingDiscount = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isPreparingDiscount,
);

export const selectIsApplyingDiscount = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isApplyingDiscount,
);

export const selectIsRemovingDiscount = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isRemovingDiscount,
);

export const selectIsPreparingBill = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isPreparingBill,
);

export const selectIsConfirmingKot = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isConfirmingKot,
);

export const selectIsKotNumberModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isKotNumberModalOpen,
);

export const selectIsReprintingKot = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isReprintingKot,
);

export const selectIsSettleModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isSettleModalOpen,
);

export const selectIsLoadingSettleOptions = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isLoadingSettleOptions,
);

export const selectIsSubmittingSettlement = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isSubmittingSettlement,
);

export const selectIsPrintReceiptPromptOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isPrintReceiptPromptOpen,
);

export const selectPendingReceiptContext = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.pendingReceiptContext,
);

export const selectIsPrintingReceipt = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isPrintingReceipt,
);

export const selectIsClientModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isClientModalOpen,
);

export const selectIsLoadingClientModalData = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isLoadingClientModalData,
);

export const selectIsAssigningClientToOrder = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isAssigningClientToOrder,
);

export const selectIsCreatingClient = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isCreatingClient,
);

export const selectIsWaiterModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isWaiterModalOpen,
);

export const selectIsLoadingWaiters = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isLoadingWaiters,
);

export const selectIsUpdatingOrderWaiter = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isUpdatingOrderWaiter,
);

export const selectIsOrderShiftModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isOrderShiftModalOpen,
);

export const selectIsPreparingOrderShift = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isPreparingOrderShift,
);

export const selectIsShiftingOrder = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isShiftingOrder,
);

export const selectIsShiftingOrderItem = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isShiftingOrderItem,
);

// Split order modal
export const selectIsOrderSplitModalOpen = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isOrderSplitModalOpen,
);

export const selectIsPreparingOrderSplit = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isPreparingOrderSplit,
);

export const selectIsRestaurantBarOutlet = createSelector(
  [(state: RunningOrderRootState) => state.auth?.user?.outlet?.business_type],
  (businessType) => businessType === BusinessType.RESTAURANT_BAR,
);

export const selectIsKotTrackingRequiredForOutlet = createSelector(
  [
    (state: RunningOrderRootState) =>
      state.auth?.user?.outlet?.kot_tracking_enabled,
  ],
  (enabled) => Boolean(enabled),
);

export const selectOutletWaiters = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.outletWaiters,
);

export const selectIsUpdatingOrderItemNotes = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isUpdatingOrderItemNotes,
);

export const selectIsRemovingOrderItem = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.isRemovingOrderItem,
);

export const selectSelectedOrderHasPendingItems = createSelector(
  [selectSelectedOrder],
  (selectedOrder) => Boolean(selectedOrder?.has_pending_items),
);

export const selectSettlementPaymentMethods = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.settlementPaymentMethods,
);

export const selectSettlementClients = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.settlementClients,
);

export const selectCompanyClients = createSelector(
  [selectRunningOrderState],
  (runningOrder) => runningOrder.companyClients,
);

export const selectHasSelectedOrder = createSelector(
  [selectSelectedOrder],
  (selectedOrder) => Boolean(selectedOrder),
);

export const selectCanShiftSelectedOrder = createSelector(
  [selectSelectedOrder, selectIsRestaurantBarOutlet, selectCanShiftOrders],
  (selectedOrder, isRestaurantBarOutlet, canShiftOrderItems) =>
    canShiftOrderItems &&
    isRestaurantBarOutlet &&
    Boolean(selectedOrder) &&
    selectedOrder?.mode === 'DINE' &&
    Boolean(selectedOrder?.table_id),
);

export const selectIsDiscountActionDisabled = createSelector(
  [
    selectCanOfferDiscounts,
    selectHasSelectedOrder,
    selectIsOrderLoading,
    selectSelectedOrderHasPendingItems,
    selectIsPreparingDiscount,
  ],
  (
    canOfferDiscounts,
    hasSelectedOrder,
    isOrderLoading,
    hasPendingItems,
    isPreparingDiscount,
  ) =>
    !canOfferDiscounts ||
    !hasSelectedOrder ||
    isOrderLoading ||
    hasPendingItems ||
    isPreparingDiscount,
);

export const selectIsBillActionDisabled = createSelector(
  [
    selectCanPrintBills,
    selectHasSelectedOrder,
    selectIsOrderLoading,
    selectSelectedOrderHasPendingItems,
    selectIsPreparingBill,
  ],
  (
    canPrintBills,
    hasSelectedOrder,
    isOrderLoading,
    hasPendingItems,
    isPreparingBill,
  ) =>
    !canPrintBills ||
    !hasSelectedOrder ||
    isOrderLoading ||
    hasPendingItems ||
    isPreparingBill,
);

export const selectIsSettleActionDisabled = createSelector(
  [
    selectHasSelectedOrder,
    selectIsOrderLoading,
    selectSelectedOrderHasPendingItems,
    selectIsPreparingBill,
    selectIsLoadingSettleOptions,
    selectIsSubmittingSettlement,
  ],
  (
    hasSelectedOrder,
    isOrderLoading,
    hasPendingItems,
    isPreparingBill,
    isLoadingSettleOptions,
    isSubmittingSettlement,
  ) =>
    !hasSelectedOrder ||
    isOrderLoading ||
    hasPendingItems ||
    isPreparingBill ||
    isLoadingSettleOptions ||
    isSubmittingSettlement,
);

export const selectIsKotActionDisabled = createSelector(
  [
    selectHasSelectedOrder,
    selectIsOrderLoading,
    selectSelectedOrderHasPendingItems,
    selectIsConfirmingKot,
  ],
  (hasSelectedOrder, isOrderLoading, hasPendingItems, isConfirmingKot) =>
    !hasSelectedOrder || isOrderLoading || !hasPendingItems || isConfirmingKot,
);

export const selectIsWaiterActionDisabled = createSelector(
  [
    selectHasSelectedOrder,
    selectIsOrderLoading,
    selectIsLoadingWaiters,
    selectIsUpdatingOrderWaiter,
  ],
  (hasSelectedOrder, isOrderLoading, isLoadingWaiters, isUpdatingOrderWaiter) =>
    !hasSelectedOrder ||
    isOrderLoading ||
    isLoadingWaiters ||
    isUpdatingOrderWaiter,
);

export const selectIsOrderShiftActionDisabled = createSelector(
  [
    selectCanShiftSelectedOrder,
    selectIsOrderLoading,
    selectIsPreparingOrderShift,
    selectIsShiftingOrder,
  ],
  (
    canShiftSelectedOrder,
    isOrderLoading,
    isPreparingOrderShift,
    isShiftingOrder,
  ) =>
    !canShiftSelectedOrder ||
    isOrderLoading ||
    isPreparingOrderShift ||
    isShiftingOrder,
);

export const selectCanShiftSelectedOrderItems = createSelector(
  [selectCanShiftSelectedOrder, selectIsShiftingOrderItem],
  (canShiftSelectedOrder, isShiftingOrderItem) =>
    canShiftSelectedOrder && !isShiftingOrderItem,
);
